"use client";

import { useCallback } from "react";
import { useState, useEffect } from "react";

export interface UseAsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export interface UseAsyncOptions {
  skip?: boolean;
}

/**
 * Generic hook for async operations
 */
export function useAsync<T>(
  asyncFunction: () => Promise<T>,
  options?: UseAsyncOptions,
): UseAsyncState<T> & { refetch: () => Promise<void> } {
  const [state, setState] = useState<UseAsyncState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  const execute = useCallback(async () => {
    if (options?.skip) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    try {
      setState({ data: null, loading: true, error: null });
      const response = await asyncFunction();
      setState({ data: response, loading: false, error: null });
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  }, [asyncFunction, options?.skip]);

  useEffect(() => {
    execute();
  }, [execute]);

  const refetch = useCallback(async () => {
    await execute();
  }, [execute]);

  return { ...state, refetch };
}

/**
 * Hook for product fetching
 */
export function useProducts() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/products`,
      );
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      setProducts(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return { products, loading, error, refetch: fetchProducts };
}

/**
 * Hook for user data fetching
 */
export function useUser() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem(
        process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "auth_token",
      );
      if (!token) {
        setUser(null);
        return;
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/users/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem(
            process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "auth_token",
          );
        }
        throw new Error("Failed to fetch user");
      }

      const data = await response.json();
      setUser(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const logout = useCallback(() => {
    localStorage.removeItem(
      process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "auth_token",
    );
    localStorage.removeItem(
      process.env.NEXT_PUBLIC_AUTH_REFRESH_KEY || "refresh_token",
    );
    setUser(null);
  }, []);

  return { user, loading, error, refetch: fetchUser, logout };
}

/**
 * Hook for wishlist
 */
export function useWishlist() {
  const [wishlist, setWishlist] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem(
          process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "auth_token",
        )
      : null;

  const fetchWishlist = useCallback(async () => {
    if (!token) {
      setWishlist([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/products/wishlist`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch wishlist");
      const data = await response.json();
      setWishlist(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  return { wishlist, loading, error, refetch: fetchWishlist };
}
